/**
 * Unified Web Audio API controller for breathing cues.
 *
 * Single AudioContext, pre-decoded buffers, played against
 * audioContext.currentTime.
 *
 * Three voices live under /public/audio/voices/{folder}/:
 *   female-1, female-2 (default — the original recordings), male.
 * Active voice is persisted in localStorage. preload() fetches every
 * voice's files so swapping voices via setVoice() or tapping a preview
 * is instant.
 *
 * KEY iOS FIX: A silent oscillator runs continuously for the entire
 * breathing session. Without continuous audio output, iOS WKWebView
 * tears down the audio session during silence gaps between cues.
 *
 * Everything here is defensive: if Web Audio is unavailable or any step
 * fails, the controller degrades to a silent no-op rather than throwing.
 */

import type { PhaseType } from '@/data/calm/protocols';

interface VoiceDef {
  label: string;
  folder: string;
  files: { inhale: string; hold: string; exhale: string };
}

type CueType = 'inhale' | 'top_up' | 'exhale' | 'hold_top' | 'hold_bottom';

// ── Voice manifest ──────────────────────────────────────────────────────────
// Two voices — Female and Male — mirroring the Calm Your Body (breathe) app, so
// the breathing cues are identical across both products. Folder names are clean
// (no spaces) and the files are inhale/hold/exhale.mp3 in each.
const VOICES: Record<string, VoiceDef> = {
  female: {
    label: 'Female',
    folder: 'female',
    files: { inhale: 'inhale.mp3', hold: 'hold.mp3', exhale: 'exhale.mp3' },
  },
  male: {
    label: 'Male',
    folder: 'male',
    files: { inhale: 'inhale.mp3', hold: 'hold.mp3', exhale: 'exhale.mp3' },
  },
};

const VOICE_STORAGE_KEY = 'renurture_breathe_voice';
const DEFAULT_VOICE = 'female';

type AudioCtxCtor = typeof AudioContext;
function getCtor(): AudioCtxCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    AudioContext?: AudioCtxCtor;
    webkitAudioContext?: AudioCtxCtor;
  };
  return w.AudioContext || w.webkitAudioContext || null;
}

function urlFor(voiceId: string, type: 'inhale' | 'hold' | 'exhale'): string {
  const v = VOICES[voiceId] || VOICES[DEFAULT_VOICE];
  return `/audio/voices/${encodeURIComponent(v.folder)}/${v.files[type]}`;
}

function buildAudioFiles(voiceId: string): Record<CueType, string> {
  return {
    inhale: urlFor(voiceId, 'inhale'),
    top_up: urlFor(voiceId, 'inhale'),
    exhale: urlFor(voiceId, 'exhale'),
    hold_top: urlFor(voiceId, 'hold'),
    hold_bottom: urlFor(voiceId, 'hold'),
  };
}

let activeVoice: string = (() => {
  try {
    const saved = localStorage.getItem(VOICE_STORAGE_KEY);
    return saved && VOICES[saved] ? saved : DEFAULT_VOICE;
  } catch {
    return DEFAULT_VOICE;
  }
})();

let audioFiles: Record<CueType, string> = buildAudioFiles(activeVoice);

let ctx: AudioContext | null = null;
let buffers: Record<string, AudioBuffer> = {};
let gainNode: GainNode | null = null;
let silentOsc: OscillatorNode | null = null;
let silentGain: GainNode | null = null;
let loaded = false;
let loading = false;

function warn(msg: string, err?: unknown): void {
  // Keep diagnostics quiet in production but useful in dev.
  if (err !== undefined) console.warn(`[CalmAudio] ${msg}`, err);
}

/** Get or create the AudioContext. Returns null if Web Audio is unavailable. */
export function getContext(): AudioContext | null {
  if (!ctx) {
    const Ctor = getCtor();
    if (!Ctor) return null;
    try {
      ctx = new Ctor();
      gainNode = ctx.createGain();
      gainNode.connect(ctx.destination);
      gainNode.gain.value = 1.0;
    } catch (e) {
      warn('AudioContext creation failed', e);
      ctx = null;
      gainNode = null;
      return null;
    }
  }
  return ctx;
}

function startSilentKeepAlive(): void {
  const ac = getContext();
  if (!ac) return;
  stopSilentKeepAlive();
  try {
    silentGain = ac.createGain();
    silentGain.gain.value = 0.001;
    silentGain.connect(ac.destination);

    silentOsc = ac.createOscillator();
    silentOsc.frequency.value = 0;
    silentOsc.connect(silentGain);
    silentOsc.start(0);
  } catch (e) {
    warn('Failed to start silent keep-alive', e);
  }
}

function stopSilentKeepAlive(): void {
  if (silentOsc) {
    try {
      silentOsc.stop();
    } catch {
      /* already stopped */
    }
    try {
      silentOsc.disconnect();
    } catch {
      /* ok */
    }
    silentOsc = null;
  }
  if (silentGain) {
    try {
      silentGain.disconnect();
    } catch {
      /* ok */
    }
    silentGain = null;
  }
}

/**
 * Resume AudioContext + start silent keep-alive.
 * Must be called from a user-gesture handler.
 */
export function unlock(): AudioContext | null {
  const ac = getContext();
  if (!ac) return null;
  if (ac.state === 'suspended') {
    ac.resume().catch((e) => warn('resume failed', e));
  }
  if (!silentOsc) startSilentKeepAlive();
  return ac;
}

/** Load a single audio file as ArrayBuffer (XHR for Capacitor scheme support). */
function loadArrayBuffer(url: string): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.responseType = 'arraybuffer';
      xhr.onload = () => {
        if (xhr.status === 200 || xhr.status === 0) {
          resolve(xhr.response as ArrayBuffer);
        } else {
          reject(new Error(`HTTP ${xhr.status}`));
        }
      };
      xhr.onerror = () => reject(new Error('XHR error'));
      xhr.send();
    } catch (e) {
      reject(e instanceof Error ? e : new Error('XHR setup failed'));
    }
  });
}

/**
 * Preload and decode audio buffers for ALL voices in one pass.
 * Idempotent and best-effort: failures are swallowed.
 */
export async function preload(): Promise<void> {
  if (loading) return;
  loading = true;
  const ac = getContext();
  if (!ac) {
    loading = false;
    return;
  }

  const allUrls = new Set<string>();
  for (const id of Object.keys(VOICES)) {
    const files = buildAudioFiles(id);
    (Object.values(files) as string[]).forEach((u) => allUrls.add(u));
  }
  const toLoad = [...allUrls].filter((u) => !buffers[u]);

  if (toLoad.length > 0) {
    await Promise.allSettled(
      toLoad.map(async (url) => {
        try {
          const arrayBuf = await loadArrayBuffer(url);
          const decoded = await ac.decodeAudioData(arrayBuf);
          buffers[url] = decoded;
        } catch (e) {
          warn(`Failed to load ${url}`, e);
        }
      }),
    );
  }

  const activeUrls = new Set<string>(Object.values(audioFiles));
  loaded = [...activeUrls].every((u) => Boolean(buffers[u]));
  loading = false;
}

/** Warm every decoded buffer at near-zero gain. Call from a user gesture. */
export function warmBuffers(): void {
  const ac = getContext();
  if (!ac || !gainNode) return;
  for (const url of Object.keys(buffers)) {
    const buf = buffers[url];
    if (!buf) continue;
    try {
      const src = ac.createBufferSource();
      src.buffer = buf;
      const g = ac.createGain();
      g.gain.value = 0.001;
      src.connect(g);
      g.connect(ac.destination);
      src.start(0);
    } catch (e) {
      warn(`warm failed for ${url}`, e);
    }
  }
}

/** Play a breathing cue immediately using the active voice's audio. */
export function playCue(phaseType: PhaseType, volume = 0.5): void {
  if (phaseType === 'rest') return;
  const url = audioFiles[phaseType as CueType];
  if (!url) return;

  const ac = getContext();
  if (!ac || !gainNode) return;
  const buf = buffers[url];
  if (!buf) return;

  if (ac.state === 'suspended') {
    ac.resume().catch((e) => warn('re-resume failed', e));
  }

  try {
    const src = ac.createBufferSource();
    src.buffer = buf;
    const g = ac.createGain();
    g.gain.value = volume;
    src.connect(g);
    g.connect(gainNode);
    src.start(0);
  } catch (e) {
    warn(`playCue error for ${phaseType}`, e);
  }
}

/** Set master volume (0..1). */
export function setVolume(v: number): void {
  if (gainNode) gainNode.gain.value = Math.max(0, Math.min(1, v));
}

/** Handle returning from background — re-resume context and restart keep-alive. */
export function handleVisibilityChange(): void {
  if (!ctx) return;
  if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
    if (ctx.state === 'suspended') {
      ctx
        .resume()
        .then(() => {
          if (!silentOsc) startSilentKeepAlive();
        })
        .catch((e) => warn('foreground resume failed', e));
    }
  }
}

/** Stop the session audio (stop keep-alive, keep context alive for re-use). */
export function stopSession(): void {
  stopSilentKeepAlive();
}

/** Full tear down — call when leaving the breathing flow entirely. */
export function destroy(): void {
  stopSilentKeepAlive();
  if (ctx) {
    ctx.close().catch(() => {
      /* ignore */
    });
    ctx = null;
    gainNode = null;
    buffers = {};
    loaded = false;
    loading = false;
  }
}

/** Check if audio is ready to play. */
export function isReady(): boolean {
  return loaded && ctx !== null && ctx.state !== 'closed';
}

// ── Voice picker API ────────────────────────────────────────────────────────

export interface VoiceOption {
  id: string;
  label: string;
}

/** Return the list of available voices for UI rendering. */
export function listVoices(): VoiceOption[] {
  return Object.keys(VOICES).map((id) => ({ id, label: VOICES[id].label }));
}

/** ID of the currently-active voice. */
export function getActiveVoice(): string {
  return activeVoice;
}

/** Switch the active voice. Persists to localStorage. */
export function setVoice(voiceId: string): void {
  if (!VOICES[voiceId]) return;
  if (voiceId === activeVoice) return;
  activeVoice = voiceId;
  try {
    localStorage.setItem(VOICE_STORAGE_KEY, voiceId);
  } catch {
    /* ignore persistence failure */
  }
  audioFiles = buildAudioFiles(voiceId);
}

/**
 * Tap-handler helper for the voice picker on TimeSelect.
 * Switches voice AND plays a preview Inhale immediately. Best-effort.
 */
export function setVoiceAndPreview(voiceId: string, volume = 0.8): void {
  if (!VOICES[voiceId]) return;
  setVoice(voiceId);
  unlock();
  void preload();
  playCue('inhale', volume);
}
